/**
 * Inline SVG charts (no library, token colours via CSS classes). Each chart carries an accessible label and, for the
 * area chart, a hidden data table so the numbers are readable without the picture.
 */
import type { ReactNode } from 'react';

function scale(values: readonly number[], w: number, h: number, pad = 2) {
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const step = values.length > 1 ? (w - pad * 2) / (values.length - 1) : 0;
  return values.map((v, i) => [pad + i * step, h - pad - ((v - min) / span) * (h - pad * 2)] as const);
}
/** Catmull-Rom to cubic Bezier: a smooth line through every point. */
function smooth(pts: readonly (readonly [number, number])[]): string {
  if (pts.length < 2) return '';
  let d = `M${pts[0]![0]},${pts[0]![1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!; const p1 = pts[i]!; const p2 = pts[i + 1]!; const p3 = pts[i + 2] ?? p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${p2[0]},${p2[1]}`;
  }
  return d;
}

/** Tiny trend line with a soft fill; decorative (the tile's number and delta carry the meaning). */
export function Sparkline({ values, width = 112, height = 36 }: { values: readonly number[]; width?: number; height?: number }) {
  const pts = scale(values, width, height);
  const line = smooth(pts);
  const last = pts[pts.length - 1]!;
  return (
    <svg className="qm-spark" viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true" focusable="false">
      <path className="qm-spark__fill" d={`${line} L${last[0]},${height} L${pts[0]![0]},${height} Z`} />
      <path className="qm-spark__line" d={line} />
      <circle className="qm-spark__dot" cx={last[0]} cy={last[1]} r="2.5" />
    </svg>
  );
}

export interface Series { name: string; values: readonly number[] }
/**
 * Area chart with up to two series, horizontal grid lines, y ticks and x labels. `format` renders tick values.
 * A visually hidden table lists every value for screen readers.
 */
export function AreaChart({ series, labels, label, format = (n) => String(n), ticks = 4, height = 220 }: {
  series: readonly Series[]; labels: readonly string[]; label: string; format?: (n: number) => string; ticks?: number; height?: number;
}) {
  const w = 640; const padL = 44; const padB = 24; const padT = 8; const padR = 8;
  const all = series.flatMap((s) => s.values);
  const max = Math.max(...all, 1);
  const nice = niceMax(max);
  const innerW = w - padL - padR; const innerH = height - padT - padB;
  const n = Math.max(...series.map((s) => s.values.length), labels.length, 2);
  const x = (i: number) => padL + (i * innerW) / (n - 1);
  const y = (v: number) => padT + innerH - (v / nice) * innerH;
  const paths = series.map((s) => smooth(s.values.map((v, i) => [x(i), y(v)] as const)));
  const tickVals = Array.from({ length: ticks + 1 }, (_, i) => (nice * i) / ticks);
  const lastIdx = n - 1;
  return (
    <figure className="qm-chart" style={{ margin: 0, minWidth: 0 }}>
      <svg className="qm-area" viewBox={`0 0 ${w} ${height}`} role="img" aria-label={label} focusable="false">
        {tickVals.map((tv) => (
          <g key={tv}>
            <line className="qm-area__grid" x1={padL} x2={w - padR} y1={y(tv)} y2={y(tv)} />
            <text className="qm-area__axis" x={padL - 8} y={y(tv) + 4} textAnchor="end">{format(tv)}</text>
          </g>
        ))}
        {paths.map((d, si) => (
          <g key={series[si]!.name}>
            <path className={`qm-area__fill-${si + 1}`} d={`${d} L${x(series[si]!.values.length - 1)},${y(0)} L${x(0)},${y(0)} Z`} />
            <path className={`qm-area__line-${si + 1}`} d={d} />
          </g>
        ))}
        {series[0] && series[0].values.length ? <circle className="qm-area__dot" cx={x(series[0].values.length - 1)} cy={y(series[0].values[series[0].values.length - 1]!)} r="4" /> : null}
        {labels.map((l, i) => (
          <text key={`${l}-${i}`} className="qm-area__axis" x={x(i)} y={height - 6} textAnchor={i === 0 ? 'start' : i === lastIdx ? 'end' : 'middle'}>{l}</text>
        ))}
      </svg>
      <table className="qm-sr-only">
        <caption>{label}</caption>
        <thead><tr><th scope="col" /> {series.map((s) => <th key={s.name} scope="col">{s.name}</th>)}</tr></thead>
        <tbody>{labels.map((l, i) => <tr key={`${l}-${i}`}><th scope="row">{l}</th>{series.map((s) => <td key={s.name}>{format(s.values[i] ?? 0)}</td>)}</tr>)}</tbody>
      </table>
    </figure>
  );
}

/** Round the axis maximum up to 1, 2, 5 x 10^k so ticks read cleanly. */
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

/** Column bars for a short series (weekly counts). The highest bar is highlighted. */
export function Bars({ values, label, labels }: { values: readonly number[]; label: string; labels?: readonly string[] }) {
  const max = Math.max(...values, 1);
  const hot = values.indexOf(max);
  return (
    <div className="qm-bars" role="img" aria-label={label} title={labels ? labels.map((l, i) => `${l}: ${values[i] ?? 0}`).join(' · ') : undefined}>
      {values.map((v, i) => <span key={i} data-hot={i === hot ? 'true' : undefined} style={{ blockSize: `${Math.max(6, (v / max) * 100)}%` }} />)}
    </div>
  );
}

export function Legend({ items }: { items: readonly { name: ReactNode }[] }) {
  return <ul className="qm-legend">{items.map((it, i) => <li key={i} data-series={i + 1}><i aria-hidden="true" />{it.name}</li>)}</ul>;
}
